import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'

interface TruncatableCellProps {
  content: string | number | React.ReactNode
  className?: string
  maxWidth?: string
  onClick?: () => void
}

const TruncatableCell: React.FC<TruncatableCellProps> = ({
  content,
  className,
  maxWidth = '120px',
  onClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)
  const cellRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkOverflow = () => {
      if (cellRef.current) {
        const element = cellRef.current
        setHasOverflow(element.scrollWidth > element.clientWidth)
      }
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [])

  const handleClick = () => {
    if (hasOverflow) {
      setIsExpanded(!isExpanded)
    }
    if (onClick) {
      onClick()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <div
      ref={cellRef}
      className={css(
        styles.cell,
        isExpanded && styles.expandedCell,
        className ? [className] : [],
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={hasOverflow ? 0 : -1}
      role={hasOverflow ? 'button' : undefined}
      style={{
        cursor: hasOverflow ? 'pointer' : 'default',
        maxWidth: isExpanded ? 'none' : maxWidth,
      }}
    >
      {content}
    </div>
  )
}

const styles = StyleSheet.create({
  cell: {
    padding: '0px 0px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#6B7280',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '0',
    transition: 'all 0.2s ease',
  },
  expandedCell: {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'unset',
    maxWidth: 'none !important',
    position: 'relative',
    zIndex: 10,
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    padding: '8px 12px',
  },
})

export default TruncatableCell
